import React, { useState } from 'react';
import { PrinterIcon } from 'lucide-react';
import { Tooltip } from '@plerionhq/design-system';
import {
  takeScreenshotAsPDF,
  useTextPDFPrint,
  type PrintMode,
} from '../../../../../utils/pdfUtils';

import { useTranslation } from 'react-i18next';

interface PDFButtonProps {
  targetRef: React.RefObject<HTMLElement | null>;
  filename?: string;
  className?: string;
  title?: string;
  format?: 'a4' | 'a3' | 'letter' | 'legal';
  orientation?: 'portrait' | 'landscape';
  printMode?: PrintMode;
  dataTestId?: string;
}

export const PDFButton: React.FC<PDFButtonProps> = ({
  targetRef,
  filename = 'document.pdf',
  className = '',
  title = 'Download as PDF',
  printMode = 'image',
  dataTestId = 'download-pdf-button',
}) => {
  const { t } = useTranslation('pleri/chat');
  const [isGenerating, setIsGenerating] = useState(false);

  // Use text-based printing hook when mode is 'text'
  const handleTextPrint = useTextPDFPrint(targetRef, { filename });

  const handlePDFGeneration = async () => {
    if (!targetRef.current || isGenerating) {
      return;
    }

    // Text mode: use react-to-print
    if (printMode === 'text') {
      setIsGenerating(true);
      try {
        await handleTextPrint();
      } catch (error) {
        console.error('Failed to generate PDF:', error);
      } finally {
        setIsGenerating(false);
      }
      return;
    }

    // Image mode: use existing screenshot method
    setIsGenerating(true);
    try {
      await takeScreenshotAsPDF(targetRef.current, {
        filename,
        quality: 1.0, // Maximum quality for perfect clarity
        backgroundColor: '#ffffff',
        margin: { top: 2, right: 2, bottom: 2, left: 2 },
      });
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      // You could add a toast notification here
    } finally {
      setIsGenerating(false);
    }
  };

  const buttonTitle =
    printMode === 'text'
      ? t('printAsPdfText')
      : printMode === 'image'
        ? t('downloadAsPdfImage')
        : title;

  return (
    <Tooltip label={buttonTitle} placement="top">
      <button
        onClick={handlePDFGeneration}
        disabled={isGenerating}
        className={`rounded-md border border-gray-300 bg-white px-2 py-1.5 text-gray-700 shadow-sm transition-all hover:border-gray-400 hover:bg-gray-50 hover:text-gray-900 hover:shadow-md focus:ring-2 focus:ring-orange-500 focus:ring-offset-1 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-gray-300 disabled:hover:bg-white disabled:hover:shadow-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:hover:border-gray-500 dark:hover:bg-gray-800 dark:hover:text-gray-100 ${className}`}
        data-testid={dataTestId}
      >
        <PrinterIcon className="h-4 w-4" />
      </button>
    </Tooltip>
  );
};
